import React, { FC, useState } from 'react';
import SpinHeader from '../SpinHeader';
import Loader from '../Loader/Loader';
import PinGate from './PinGate';
import Confetti from './Confetti';
import useLeaderboard, { LeaderboardRow } from './useLeaderboard';
import badge1st from '../../Utils/images/1st_winner_badge.png';
import badge2nd from '../../Utils/images/2nd_winner_badge.png';
import badge3rd from '../../Utils/images/3rd_winner_badge.png';
import classes from './Leaderboard.module.css';

const COLUMNS: {
  key: keyof LeaderboardRow;
  label: string;
  optional?: boolean;
  align?: 'center' | 'right';
  capitalize?: boolean;
}[] = [
  { key: 'rank', label: 'RANK', align: 'center' },
  { key: 'name', label: 'NAME', capitalize: true },
  { key: 'store', label: 'STORE NUMBER' },
  { key: 'ring', label: 'RING NAME' },
  { key: 'targeted_price', label: 'TARGET PRICE', align: 'right' },
  { key: 'total_price', label: 'BUILD PRICE', optional: true, align: 'right' },
  { key: 'price_diff', label: 'DIFFERENCE', align: 'right' },
  { key: 'total_duration', label: 'TIME', align: 'center' },
];

const LEADERBOARD_PIN = process.env.REACT_APP_LEADERBOARD_PIN;

const RANK_CLASSES = [ classes.rankGold, classes.rankSilver, classes.rankBronze ];
const RANK_BADGES = [ badge1st, badge2nd, badge3rd ];

const RankCell: FC<{ rank: number }> = ({ rank }) => {
  const badge = RANK_BADGES[rank - 1];

  return badge
    ? <img className={classes.badge} src={badge} alt={`Rank ${rank}`}/>
    : <>{rank || '-'}</>;
};

const Leaderboard: FC = () => {
  const [ isUnlocked, setIsUnlocked ] = useState(!LEADERBOARD_PIN);
  const { rows, isLoading, error } = useLeaderboard(isUnlocked);

  const columns = COLUMNS.filter(
    (column) => !column.optional || rows.some((row) => row[column.key])
  );

  const message = error
    ? 'We could not load the leaderboard.'
    : !isLoading && rows.length === 0
      ? 'No entries yet.'
      : '';

  return (
    <main className={classes.root}>
      <div className={classes.header}>
        <SpinHeader/>
      </div>
      <h1 className={classes.title}>LEADERBOARD</h1>
      {!isUnlocked && (
        <PinGate pin={LEADERBOARD_PIN as string} onUnlock={() => setIsUnlocked(true)}/>
      )}
      {isUnlocked && !isLoading && rows.length > 0 && <Confetti/>}
      {isUnlocked && <div className={classes.tableWrap}>
        <table className={classes.table}>
          <thead>
          <tr>
            {columns.map((column) => (
              <th key={column.key} className={column.align ? classes[column.align] : ''}>
                {column.label}
              </th>
            ))}
          </tr>
          </thead>
          {isLoading ? (
            <tbody>
            <tr className={classes.loaderRow}>
              <td colSpan={columns.length} className={classes.loaderCell}>
                <Loader position={'absolute'}/>
              </td>
            </tr>
            </tbody>
          ) : (
            <tbody>
            {rows.map((row, index) => (
              <tr key={`${row.rank}-${index}`} className={RANK_CLASSES[Number(row.rank) - 1] ?? ''}>
                {columns.map((column) => (
                  <td
                    key={column.key}
                    className={`${column.align ? classes[column.align] : ''} ${column.capitalize ? classes.capitalized : ''}`}
                  >
                    {column.key === 'rank' ? (
                      <RankCell rank={Number(row.rank)}/>
                    ) : (
                      row[column.key]
                    )}
                  </td>
                ))}
              </tr>
            ))}
            </tbody>
          )}
        </table>
        {message && <p className={classes.message}>{message}</p>}
      </div>}
    </main>
  );
};

export default Leaderboard;
